/**
 * this contains all the layouts we use in the app
 *
 */
import React, { memo, useEffect, useState } from "react";
import i18n from "i18next";
import LogoLoaderDE from "@/assets/icons/LoaderGermanLogo.svg?react";
import LogoLoaderEN from "@/assets/icons/LoaderEnglishLogo.svg?react";
import { Outlet, ScrollRestoration, useLocation, useNavigation } from "react-router";
import { useSessionContext } from "@/contexts/sessionContext";
import { SettingRoutes } from "@/config/RouteMap";
import { ViewsFiltersContextProvider } from "@/contexts/viewsFiltersContext";

/**
 * this is just a loading layout, that can be nested,
 * its height and width is 100%
 */
export const LoadingLayout = memo(({ lang = undefined }) => {
  return (
    <div className="js-loading-layout" role="status" aria-live="polite" aria-label="Loading content">
      {(lang || i18n.language) === "en" ? <LogoLoaderEN aria-hidden="true" /> : <LogoLoaderDE aria-hidden="true" />}
    </div>
  );
});

/**
 * this is a full page loading layout,
 * this has 100vh height
 */
export const FullPageLoadingLayout = memo(({ lang = undefined }) => {
  return (
    <div
      style={{
        height: "100vh",
      }}
    >
      <LoadingLayout lang={lang} />
    </div>
  );
});

export const JSPageLayout = memo(() => {
  const location = useLocation();

  const { session } = useSessionContext();
  const isUSDemo = (session?.UserEmail?.includes("demov2") && session?.UserID === "64") || false;
  const isSettingsPage = Object.values(SettingRoutes).some((route) => location.pathname.includes(route));
  const [sidebarCollapsed, setSidebarCollapsed] = useState(localStorage.getItem("navcollapse") === "true");

  useEffect(() => {
    const handleResize = () => {
      // Set Zoom Level and don't go below 1280px width
      const currentZoom = document.body.style.zoom;
      let newZoom;

      if (window.innerWidth < 1280) {
        newZoom = (window.innerWidth * 100) / 1280 - 1 + "%";
      } else if (window.innerWidth > 3840) {
        newZoom = (window.innerWidth * 100) / 3840 - 1 + "%";
      } else {
        newZoom = "0%";
      }

      // update zoom if it's different to prevent infinite loop
      if (currentZoom !== newZoom) {
        document.body.style.zoom = newZoom;
      }
    };

    handleResize(); // Initial call to set zoom level
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    localStorage.setItem("navcollapse", sidebarCollapsed);
  }, [sidebarCollapsed]);

  if (Object.keys(session || {}).length === 0) return null;


  return (
    <div className={`js-page-layout ${isUSDemo ? "is-us-demo" : ""}`}>
      <div id="app-main" className={`${sidebarCollapsed ? "collapsed" : "expanded"}`}>
        <ScrollRestoration />
        <NestedViewsFiltersContextProvider isSettingsPage={isSettingsPage} />
      </div>
    </div>
  );
});
JSPageLayout.displayName = "JSPageLayout";



/**
 * this section is splitted out from JSPageLayout
 * as sidebar collapsed/expanded was triggering re-render of the viewsFiltersContext
 * which was re-rendering all jobs cards
 */
const NestedViewsFiltersContextProvider = memo(({ isSettingsPage = false }) => {
  const navigation = useNavigation();

  return (
    <ViewsFiltersContextProvider>
      {navigation.state === "loading" && !isSettingsPage ? <LoadingLayout /> : <Outlet />}
    </ViewsFiltersContextProvider>
  );
});
NestedViewsFiltersContextProvider.displayName = "NestedViewsFiltersContextProvider";

/**
 * this layout further holds the login page and forgot password page
 * layout made common we we have carousel and footer common in both pages
 */
export const LoginPageLayout = memo(() => {
  return (
    <div className="pageWrapper">
      <div className="login login-vertical-gap">
        <div className="container-div-login">
          <Outlet />
        </div>
      </div>
    </div>
  );
});
LoginPageLayout.displayName = "LoginPageLayout";
